/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { createFeatureSelector, createSelector, createSelectorFactory, DefaultProjectorFn, resultMemoize } from '@ngrx/store';
import { mediaFeature, MediaState, selectAllMediaConnections, selectMediaConnectionEntities } from './media.reducer';
import { RxStompState } from '@stomp/rx-stomp';
import {
    selectClusterConfiguration,
    selectClusterConfigurationByIdMap,
    selectCtcStatus,
    selectPrimaryConnectionClusterName,
    selectQuiescence
} from '../../configuration/+state/configuration.selectors';
import { selectAuthRecordClusterName, selectMemoizedUserAuthenticationRecord } from '../../user/+state/user.selectors';
import { MediaConnection } from '../../core/model/media-connection';
import { MediaService } from '../services/media.service';
import {
    selectAlertOutputToDevice,
    selectAlertOutputToHeadsets,
    selectCCHubAttached,
    selectCCHubAttachedAndInitialized,
    selectCCHubHeadsetConnected,
    selectCCHubVolumeStatus,
    selectIrrOutputToDevice,
    selectIrrOutputToHeadsets
} from './cchub.selectors';
import { Cluster, ConnectionStatus } from 'CalltakingCoreApi';
import {
    selectRtiAttached,
    selectSamAttached,
    selectUsbAlertOutputToDevice,
    selectUsbAlertOutputToHeadsets,
    selectUsbAttached,
    selectUsbAttachedAndInitialized,
    selectUsbFirmwareChecked,
    selectUsbHeadsetConnected,
    selectUsbIrrOutputToDevice,
    selectUsbIrrOutputToHeadsets
} from '../../usb/+state/usb.selectors';
import { CtcState } from '../../configuration/model/CtcState';
import { AudioDeviceSettings } from '../../settings/model/settings.model';

export const selectMediaConnectionState = createFeatureSelector<MediaState>(mediaFeature.name);

export const selectMediaConnectionMap = createSelector(selectMediaConnectionState, selectMediaConnectionEntities);

export const selectMediaConnections = createSelector(selectMediaConnectionState, selectAllMediaConnections);

const _selectMediaConnection = (uuid: string) => createSelector(selectMediaConnectionMap,  (mediaConnectionMap) => mediaConnectionMap[uuid]);

export const selectMediaConnectionPassword = (uuid: string) => createSelector(_selectMediaConnection(uuid), (mediaConnection) => mediaConnection.configuration.password);

export const selectMediaConnectionIsConnected = (uuid: string) => createSelector(_selectMediaConnection(uuid), (mediaConnection) => Boolean(mediaConnection.connected));

export const selectMediaConnectionsByClusterNameMap = createSelector(selectMediaConnections, (mediaConnections) =>
    mediaConnections.reduce<{ [key: string]: MediaConnection }>((map, mediaConnection) => ({ ...map, [mediaConnection.name]: mediaConnection }), {})
);

export const selectIsPhoneRegistered = createSelector(selectMediaConnections, (mediaConnections) =>
    mediaConnections.some((mediaConnection) => mediaConnection.connected && mediaConnection.registered)
);

export const selectIsPhoneFullyRegistered = createSelector(selectMediaConnections, (mediaConnections) =>
    mediaConnections.length && mediaConnections.every((mediaConnection) => mediaConnection.connected && mediaConnection.registered)
);

export const selectRegisteredMediaCount = createSelector(
    selectMediaConnections,
    (mediaConnections) => mediaConnections.filter((mediaConnection) => mediaConnection.connected && mediaConnection.registered).length
);

export const selectOrphanedSessions = createSelector(selectMediaConnections, (mediaConnections) =>
    mediaConnections
        .filter((mediaConnection) => (!mediaConnection.connected || !mediaConnection.registered) && mediaConnection.session)
        .map((deadMediaConnections) => deadMediaConnections.session)
        .filter((deadSession) => deadSession.state !== 'ENDED' && deadSession.state !== 'FAILED')
        .map((deadSession) => deadSession.id as string)
);

export const selectCallVolume = createSelector(selectMediaConnectionState, (state) => state.callVolume);

export const selectAlertVolume = createSelector(selectMediaConnectionState, (state) => state.alertVolume);

export const selectIrrVolume = createSelector(selectMediaConnectionState, (state) => state.irrVolume);

export const selectHasUserMedia = createSelector(selectMediaConnectionState, (state) => state.hasUserMedia);

export const selectUserMediaDevices = createSelector(selectMediaConnectionState, (state) => state.userMediaDevices);

export const selectAvailableMediaDevices = createSelector(selectUserMediaDevices, (userMediaDevices) => userMediaDevices?.filter(MediaService.selectableMediaDeviceOptionsFilter));

export const selectUserAudioInputMediaDevices = createSelector(selectAvailableMediaDevices, (userMediaDevices) => userMediaDevices?.filter((md) => md.kind === 'audioinput'));

export const selectUserAudioOutputMediaDevices = createSelector(selectAvailableMediaDevices, (userMediaDevices) => userMediaDevices?.filter((md) => md.kind === 'audiooutput'));

export const selectStunFlag = createSelector(selectMediaConnectionState, (state) => state.useStunIfAvailable);
export const selectJitterBufferTarget = createSelector(selectMediaConnectionState, (state) => state.jitterBufferTarget);

export const selectUserMediaInputDeviceId = createSelector(selectMediaConnectionState, (state) => state.userMediaInputDeviceId);

export const selectUserMediaInputDevice = createSelector(
    selectUserAudioInputMediaDevices,
    selectUserMediaInputDeviceId,
    selectCCHubAttached,
    selectSamAttached,
    selectRtiAttached,
    (mediaDevices, deviceId, ccHubAttached, samAttached, rtiAttached) =>
        ccHubAttached || samAttached || rtiAttached ? undefined : mediaDevices?.find((md) => md.deviceId === deviceId)
);
export const selectUserMediaOutputDeviceId = createSelector(selectMediaConnectionState, (state) => state.userMediaOutputDeviceId);

export const selectAlertMediaOutputDeviceId = createSelector(selectMediaConnectionState, (state) => state.audibleAlertOutputDeviceId);

export const selectIrrMediaOutputDeviceId = createSelector(selectMediaConnectionState, (state) => state.irrOutputDeviceId);

export const selectUserMediaOutputDevice = createSelector(
    selectUserAudioOutputMediaDevices,
    selectUserMediaOutputDeviceId,
    selectCCHubAttached,
    selectSamAttached,
    selectRtiAttached,
    (mediaDevices, deviceId, ccHubAttached, samAttached, rtiAttached) =>
        ccHubAttached || samAttached || rtiAttached ? undefined : mediaDevices?.find((md) => md.deviceId === deviceId)
);

export const selectAlertOutputDeviceEnabled = createSelector(
    selectCCHubAttached,
    selectSamAttached,
    selectRtiAttached,
    selectUsbAlertOutputToDevice,
    selectAlertOutputToDevice,
    (ccHubAttached, samAttached, rtiAttached, usbAlertOutputToDevice, alertOutputToDevice) =>
        !(ccHubAttached || samAttached || rtiAttached) || (ccHubAttached && alertOutputToDevice) || ((samAttached || rtiAttached) && usbAlertOutputToDevice)
);

export const selectAlertMediaOutputDevice = createSelector(
    selectUserAudioOutputMediaDevices,
    selectAlertMediaOutputDeviceId,
    selectAlertOutputDeviceEnabled,
    (mediaDevices, deviceId, alertOutputDeviceEnabled) => (alertOutputDeviceEnabled ? mediaDevices?.find((md) => md.deviceId === deviceId) : undefined)
);

export const selectIrrOutputDeviceEnabled = createSelector(
    selectCCHubAttached,
    selectSamAttached,
    selectRtiAttached,
    selectUsbIrrOutputToDevice,
    selectIrrOutputToDevice,
    (ccHubAttached, samAttached, rtiAttached, usbIrrOutputToDevice, irrOutputToDevice) =>
        !(ccHubAttached || samAttached || rtiAttached) || (ccHubAttached && irrOutputToDevice) || ((samAttached || rtiAttached) && usbIrrOutputToDevice)
);

export const selectIrrOutputDevice = createSelector(
    selectUserAudioOutputMediaDevices,
    selectIrrMediaOutputDeviceId,
    selectIrrOutputDeviceEnabled,
    (mediaDevices, deviceId, irrOutputDeviceEnabled) => (irrOutputDeviceEnabled ? mediaDevices?.find((md) => md.deviceId === deviceId) : undefined)
);

export const selectSipDebugEnabled = createSelector(selectMediaConnectionState, (state) => state.sipDebugEnable);

export const selectWebsocketConnectionState = (clusterName: string, primary?: boolean) => createSelector(selectMediaConnectionState, selectPrimaryConnectionClusterName, (state, primaryClusterName) => {
    if(clusterName && typeof primary !== 'boolean') {
        return state.ctcWebsocketState[clusterName] ? state.ctcWebsocketState[clusterName].connectionStatus : RxStompState.CLOSED;
    }
    return state.ctcWebsocketState[primaryClusterName] ? state.ctcWebsocketState[primaryClusterName].connectionStatus : RxStompState.CLOSED;
});

export const selectWebsocketEnabled= (clusterName: string) => createSelector(selectWebsocketConnectionState(clusterName), (state) => state === RxStompState.CONNECTING || state === RxStompState.OPEN);

export function ctcAvailabilityIsequal(a: { clusterName: string, available: boolean }, b: { clusterName: string, available: boolean }): boolean {
    return a.available === b.available && a.clusterName === b.clusterName;
}

export const ctcAvailabilityMemoize = (projectorFn: DefaultProjectorFn<{ clusterName: string, available: boolean }>) => resultMemoize(projectorFn, ctcAvailabilityIsequal);

export const selectCtcAvailability = (uuid: string) => createSelectorFactory(ctcAvailabilityMemoize)(selectCtcStatus, selectClusterConfigurationByIdMap, selectCtcMultipleWebsocketState,  (status: {[key: string]: boolean}, clusterMap: {[p: string]: Cluster}, websocketStatus: {[p: string]: CtcState}) => {
    return ({ clusterName: clusterMap[uuid]?.name, available: Boolean(status[clusterMap[uuid].name] && websocketStatus[clusterMap[uuid]?.name]?.connectionStatus === RxStompState.OPEN) });
});



export const selectPreferredOutboundCallClusterName = createSelector(selectAuthRecordClusterName, selectMediaConnectionsByClusterNameMap, selectQuiescence,
    (homeCluster, mediaConnectionsMap, quiescence) => !quiescence[homeCluster] && mediaConnectionsMap[homeCluster]?.registered ? homeCluster : Object.values(mediaConnectionsMap).filter((mediaConnection) => !quiescence[mediaConnection.name]).find((connection) => connection.registered)?.name
);

export const selectUsbPeripheralReady = createSelector(
    selectCCHubAttached,
    selectCCHubAttachedAndInitialized,
    selectCCHubHeadsetConnected,
    selectUsbAttached,
    selectUsbFirmwareChecked,
    selectUsbHeadsetConnected,
    (ccHubAttached, ccHubConnectedAndInitialized, ccHubHeadsetConnected, usbAttached, usbFirmwareChecked, usbHeadsetConnected) => {
    let ccHubCheck = ccHubAttached && ccHubConnectedAndInitialized && ccHubHeadsetConnected || !ccHubAttached;
    let usbCheck = usbAttached && usbFirmwareChecked && usbHeadsetConnected || !usbAttached;
    return ccHubCheck && usbCheck;
});

export const selectMediaConnectionStatus = createSelector(selectMemoizedUserAuthenticationRecord, selectMediaConnections, (user, mediaConnections) =>
    user ? mediaConnections.reduce<{ [key: string]: ConnectionStatus }>((map, mediaConnection) => ({ ...map, [mediaConnection.asteriskAddress]: MediaService.getConnectionStatus(mediaConnection) }), {}) : undefined);


export const selectCtcMultipleWebsocketState = createSelector(
    selectMediaConnectionState,
    (state: MediaState) =>  state.ctcWebsocketState);

export const selectClusterAddressMapWithState = createSelector(
    selectClusterConfiguration,
    selectCtcMultipleWebsocketState
    , (clusters, status) => {
        return clusters.map((cluster) => {
            return {
                name: cluster.name,
                label: cluster.clusterLabel,
                state: status[cluster.name]?.connectionStatus,
            };
        });
    });



export const selectWebSocketStatusesMultipleWebsocketConnection = createSelector(
    selectCtcMultipleWebsocketState,
    (ctcState) => Object.entries(ctcState).map(([key, value]) => ({ name: key, connected: value.connectionStatus === RxStompState.OPEN }))
);

export const selectWebsocketConnectionStatusOverride = (clusterName: string) => createSelector(
    selectMediaConnectionState,
    (mediaState) => {
        return typeof mediaState.overrideCtcWebsocketConnection[clusterName] === 'boolean' ? mediaState.overrideCtcWebsocketConnection[clusterName] : undefined;
    }
);

export const selectIsPrimaryConnectionActive = createSelector(selectPrimaryConnectionClusterName, selectCtcMultipleWebsocketState, (primaryClusterName, ctcState) => {
    return primaryClusterName && ctcState[primaryClusterName] ? ctcState[primaryClusterName].connectionStatus === RxStompState.OPEN : false;
});

export const selectConnectionStatusByClusterName = (clusterName: string) => createSelector(
    selectCtcMultipleWebsocketState,
    (ctcState) => ctcState[clusterName]?.connectionStatus
);

export const selectInitializedMediaConnection = (uuid: string, clusterName: string) => createSelector(_selectMediaConnection(uuid), selectConnectionStatusByClusterName(clusterName), (mediaConnection, connectionStatus) =>
    Boolean(mediaConnection && mediaConnection.socket && mediaConnection.configuration?.uri && mediaConnection.configuration?.contact_uri && mediaConnection.configuration?.extra_headers?.length && mediaConnection.configuration?.password && connectionStatus === RxStompState.OPEN) ? mediaConnection : undefined
);

export const selectAlertToHeadset = createSelector(
    selectCCHubAttachedAndInitialized,
    selectUsbAttachedAndInitialized,
    selectUsbAlertOutputToHeadsets,
    selectAlertOutputToHeadsets,
    (ccHubAttachedAndInitialized, usbAttachedAndInitialized, usbAlertToHeadsets, ccHubAlertToHeadsets) =>
        ccHubAttachedAndInitialized ? ccHubAlertToHeadsets : usbAttachedAndInitialized ? usbAlertToHeadsets : undefined
);

export const selectAlertToDevice = createSelector(
    selectCCHubAttachedAndInitialized,
    selectUsbAttachedAndInitialized,
    selectUsbAlertOutputToDevice,
    selectAlertOutputToDevice,
    (ccHubAttachedAndInitialized, usbAttachedAndInitialized, usbAlertToDevice, ccHubAlertToDevice) =>
        ccHubAttachedAndInitialized ? ccHubAlertToDevice : usbAttachedAndInitialized ? usbAlertToDevice : undefined
);

export const selectIrrToHeadsets = createSelector(
    selectCCHubAttachedAndInitialized,
    selectUsbAttachedAndInitialized,
    selectUsbIrrOutputToHeadsets,
    selectIrrOutputToHeadsets,
    (ccHubAttachedAndInitialized, usbAttachedAndInitialized, usbIrrOutputToHeadsets, ccHubIrrOutputToHeadsets) =>
        ccHubAttachedAndInitialized ? ccHubIrrOutputToHeadsets : usbAttachedAndInitialized ? usbIrrOutputToHeadsets : undefined
);

export const selectIrrToDevice = createSelector(
    selectCCHubAttachedAndInitialized,
    selectUsbAttachedAndInitialized,
    selectUsbIrrOutputToDevice,
    selectIrrOutputToDevice,
    (ccHubAttachedAndInitialized, usbAttachedAndInitialized, usbIrrToDevice, ccHubIrrToDevice) =>
        ccHubAttachedAndInitialized ? ccHubIrrToDevice : usbAttachedAndInitialized ? usbIrrToDevice : undefined
);

export const selectVolumeStatus = createSelector(selectCCHubAttachedAndInitialized, selectCCHubVolumeStatus, (ccHubAttachedAndInitialized, volumeStatus) =>
    ccHubAttachedAndInitialized ? volumeStatus : undefined
);

export const selectActiveAudioSettings = createSelector(
    selectUserMediaInputDeviceId,
    selectUserMediaOutputDeviceId,
    selectAlertMediaOutputDeviceId,
    selectIrrMediaOutputDeviceId,
    selectCallVolume,
    selectAlertVolume,
    selectIrrVolume,
    selectAlertToHeadset,
    selectAlertToDevice,
    selectIrrToHeadsets,
    selectIrrToDevice,
    selectVolumeStatus,
    (
        mainInputDeviceId,
        mainOutputDeviceId,
        alertOutputDeviceId,
        irrOutputDeviceId,
        callOutputVolume,
        alertOutputVolume,
        irrOutputVolume,
        alertToHeadsets,
        alertToDevice,
        irrToHeadsets,
        irrToDevice,
        volumeStatus
    ) => {
        return {
            mainInputDeviceId,
            mainOutputDeviceId,
            alertOutputDeviceId,
            irrOutputDeviceId,
            callOutputVolume,
            alertOutputVolume,
            irrOutputVolume,
            ...(alertToHeadsets !== undefined && { alertToHeadsets }),
            ...(alertToDevice !== undefined && { alertToDevice }),
            ...(irrToHeadsets !== undefined && { irrToHeadsets }),
            ...(irrToDevice !== undefined && { irrToDevice }),
            ...(volumeStatus !== undefined && { volumeStatus })
        } as AudioDeviceSettings;
    }
);
